import { useContext, useState, type ReactElement } from "react";
import { useLocation } from "react-router";

import { fetchCsv, SaveFileContext, type DownloadOutcome } from "../api/download.js";
import { loginUrlAfterUnauthorized } from "../app/session-expiry.js";
import { useGo } from "../app/use-go.js";

import { loadFailureText, timeOf } from "./load-failure.js";

/**
 * 「CSV でダウンロード」のボタン（管理センターの CSV はすべてこれを使う。docs/screens/33・36・43・46・49）。
 *
 * <b>押すと fetch で受け取り、Blob にして保存させる</b>（<c>api/download.ts</c>）。画面は移らない。
 * 待っている間はボタンを押せなくし「作成中…」と出す（二度押しで同じ CSV を何度も作らせない）。
 * <b>失敗しても画面を残し、断りの欄（<c>alert-danger</c>）に出す</b>——素のリンクだと、SWA が打ち切ったときに
 * SWA の素の文字だけの画面へ移ってしまう。未ログイン（401、またはログインの画面へ移された）はログインへ（戻り先にこの画面）。
 */

/** ボタンの文言。 */
export const csvDownloadLabel = "CSV でダウンロード";

/** 待っている間の文言。 */
export const csvBusyLabel = "作成中…";

/**
 * 応答に名前が無いときの名前（サーバの規則と同じ <c>{prefix}_yyyyMMdd_HHmmss.csv</c>。日本時間）。
 */
export const csvFileName = (prefix: string, now: Date): string => {
    const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Tokyo", hourCycle: "h23",
        year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(now).map((o) => [o.type, o.value]));
    const at = `${parts["year"] ?? ""}${parts["month"] ?? ""}${parts["day"] ?? ""}_${parts["hour"] ?? ""}${parts["minute"] ?? ""}${parts["second"] ?? ""}`;
    return `${prefix}_${at}.csv`;
};

/** 受け取れなかったときの文（「CSV を作れませんでした。」から始める 1 つの文）。 */
export const csvFailureMessage = (failure: Extract<DownloadOutcome, { kind: "failed" }>, at: Date): string => {
    const lead = "CSV を作れませんでした。";
    const { status, errors, notCsv } = failure;
    if (notCsv === true) {
        return `${lead}サーバの応答が CSV ではありませんでした（${timeOf(at)}）。`
            + "時間をおいてもう一度お試しください。解決しないときは、この時刻を添えてお問い合わせください。";
    }
    // API は権限が無いときも 404 で断る（存在を明かさない）
    if (status === 404) {
        return `${lead}対象が見つからないか、ダウンロードする権限がありません。画面を開き直してから、もう一度お試しください。`;
    }
    // 条件の断り（期間の誤りなど）。文はサーバが返す
    const messages = errors === undefined ? [] : Object.values(errors).flat();
    if (status === 400 && messages.length > 0) {
        return `${lead}${messages.join(" ")}`;
    }
    const { reason, hint } = loadFailureText(status === undefined ? { at } : { status, at });
    return `${lead}${reason}${hint}`;
};

/** ボタンと断りの欄が使う状態。 */
export interface CsvDownloadState {
    readonly busy: boolean;
    /** 断りの文。無ければ null。 */
    readonly error: string | null;
    readonly start: () => void;
}

/**
 * 押したときの動き。<c>dialog</c> を渡すと、<b>受け取る前に</b>その文で確認のダイアログを出す（キャンセルなら何もしない）。
 * <c>filePrefix</c> は応答に名前が無いときの名前の頭（<c>csvFileName</c>）。
 */
export const useCsvDownload = ({ url, filePrefix, dialog }: {
    readonly url: string;
    readonly filePrefix: string;
    readonly dialog?: (() => string) | undefined;
}): CsvDownloadState => {
    const go = useGo();
    const save = useContext(SaveFileContext);
    const { pathname, search } = useLocation();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = async (): Promise<void> => {
        setBusy(true);
        setError(null);
        const outcome = await fetchCsv(url);
        switch (outcome.kind) {
            case "file": {
                save(outcome.blob, outcome.fileName ?? csvFileName(filePrefix, new Date()));
                break;
            }
            case "unauthorized": {
                go(loginUrlAfterUnauthorized(pathname + search, false));
                return;
            }
            case "failed": {
                setError(csvFailureMessage(outcome, new Date()));
                break;
            }
        }
        setBusy(false);
    };

    return {
        busy,
        error,
        start: () => {
            if (busy) {
                return;
            }
            if (dialog !== undefined && !globalThis.confirm(dialog())) {
                return;
            }
            void run();
        },
    };
};

/** ボタン（<c>button</c>。見た目は呼ぶ側が渡す。<c>btn-secondary</c> など）。 */
export function CsvDownloadButton({ download, className }: {
    readonly download: CsvDownloadState;
    readonly className: string;
}): ReactElement {
    return (
        <button
            type="button"
            className={className}
            disabled={download.busy}
            aria-busy={download.busy}
            onClick={download.start}
            data-csv-button=""
        >
            {download.busy ? csvBusyLabel : csvDownloadLabel}
        </button>
    );
}

/** 断りの欄（無ければ出さない）。 */
export function CsvDownloadError({ download }: { readonly download: CsvDownloadState }): ReactElement | null {
    return download.error === null
        ? null
        : <div className="alert-danger mt-2" role="alert" data-csv-error="">{download.error}</div>;
}
